import type { ComponentType, ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";

import type { ComponentIconProps } from "@similarweb/icons";
import { ChevronDown } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { SubsectionItem } from "@similarweb/ui-components";

import type { ISubSectionItem, ISubSectionItemBase } from "app/components/sidebar3/types";
import { translate } from "app/filters/i18nFilter";
import type {
    EPrimarySideBar3IdType,
    EPrimarySideBar3StateType,
    IPrimaryBarData,
} from "app/reducers/_reducers/PrimarySidebar3Reducer";

import { ChildrenContainer, ExpandableMenuItemWrapper, ExpandChevronWrapper, IconPlaceholder } from "./styles";

interface IExpandableMenuItemProps {
    parentItem: ISubSectionItem;
    isPrimarySidebarOpened: boolean;
    primaryBarData: IPrimaryBarData;
    onSetSelectedPrimarySectionChange: (state: EPrimarySideBar3StateType, id: EPrimarySideBar3IdType) => void;
    itemCounter: number;
}

const EmptyIcon: ComponentType<ComponentIconProps> = () => <IconPlaceholder />;

export const ExpandableMenuItem = ({
    parentItem,
    isPrimarySidebarOpened,
    primaryBarData,
    onSetSelectedPrimarySectionChange,
    itemCounter,
}: IExpandableMenuItemProps): ReactElement => {
    const [isHovered, setIsHovered] = useState(false);

    const visibleChildren = useMemo(
        () => parentItem.children?.filter((child) => !child.isHidden) ?? [],
        [parentItem.children],
    );

    const isAnyChildActive = useMemo(
        () => visibleChildren.some((child) => primaryBarData.id === child.id),
        [visibleChildren, primaryBarData.id],
    );

    const isParentDirectlyActive = primaryBarData.id === parentItem.id;
    const isExpanded = isHovered && isPrimarySidebarOpened;
    const isParentSelected = isParentDirectlyActive || (!isExpanded && isAnyChildActive);

    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
    }, []);

    const createClickHandler = useCallback(
        (item: ISubSectionItemBase, counter: number) => (id: string) => {
            if (item.isLocked) {
                item.onClickLocked?.();
                return;
            }
            onSetSelectedPrimarySectionChange(item.state, id as EPrimarySideBar3IdType);
            item.onClick?.(
                item.state,
                id,
                counter,
                Boolean(item.isLocked),
                item.badgeData?.badgeType,
                item.ParentTrackName,
            );
        },
        [onSetSelectedPrimarySectionChange],
    );

    const handleParentClick = useMemo(
        () => createClickHandler(parentItem, itemCounter),
        [createClickHandler, parentItem, itemCounter],
    );

    const hasChildren = visibleChildren.length > 0;
    const shouldShowChevron = !parentItem.upsellBadgeComponent && hasChildren && isPrimarySidebarOpened;

    const expandChevron = useMemo(
        () =>
            shouldShowChevron ? (
                <ExpandChevronWrapper $isExpanded={isExpanded}>
                    <ChevronDown
                        size="xs"
                        color={isParentSelected ? colorsPalettes.carbon[0] : colorsPalettes.carbon[400]}
                    />
                </ExpandChevronWrapper>
            ) : undefined,
        [shouldShowChevron, isExpanded, isParentSelected],
    );

    const parentBadgeComponent: ReactElement | undefined = parentItem.upsellBadgeComponent ?? expandChevron;

    return (
        <ExpandableMenuItemWrapper
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-automation={`sidebar-expandable-menu-${parentItem.id}`}
        >
            <SubsectionItem
                iconColor={colorsPalettes.indigo[300]}
                key={parentItem.title}
                itemText={parentItem.title ? translate(parentItem.title) : undefined}
                itemType={parentItem.itemType}
                onItemClick={handleParentClick}
                navBadgeType={parentItem.badgeData?.badgeType}
                badgeText={parentItem.badgeData?.badgeText}
                id={parentItem.id}
                icon={parentItem.icon}
                isCollapsed={!isPrimarySidebarOpened}
                isSelected={isParentSelected}
                isLocked={parentItem.isLocked ? !parentItem.upsellBadgeComponent : undefined}
                badgeComponent={parentBadgeComponent}
            />
            <ChildrenContainer $isExpanded={isExpanded}>
                {visibleChildren.map((child, index) => {
                    const childCounter = itemCounter + index + 1;
                    return (
                        <SubsectionItem
                            key={child.title}
                            iconColor={colorsPalettes.indigo[300]}
                            itemText={child.title ? translate(child.title) : undefined}
                            itemType={child.itemType}
                            onItemClick={createClickHandler(child, childCounter)}
                            navBadgeType={child.badgeData?.badgeType}
                            badgeText={child.badgeData?.badgeText}
                            id={child.id}
                            icon={EmptyIcon}
                            isCollapsed={!isPrimarySidebarOpened}
                            isSelected={primaryBarData.id === child.id}
                            isLocked={child.isLocked ? !child.upsellBadgeComponent : undefined}
                            badgeComponent={child.upsellBadgeComponent}
                        />
                    );
                })}
            </ChildrenContainer>
        </ExpandableMenuItemWrapper>
    );
};
